<template>
  <div class="promocode-wrapper d-flex fl-col fl-j-c fl-a-c">
    <div class="inputs-wrapper" style="padding-top: 10%">
      <div class="mx-auto flex w-[96%] gap-2.5">
        <SInputGroup class="h-20 min-w-0 flex-1">
          <SInputGroupInput
            class="h-full text-[26px]"
            :placeholder="inputPlaceholder"
            :model-value="voucherCode"
            @update:model-value="(v) => (voucherCode = String(v))"
          />
          <SInputGroupAddon align="inline-end">
            <SvgIcon
              :iconContent="editPenIconContent"
              :iconAlt="editPenIconAlt"
              :iconStyle="editPenIconStyle"
            />
          </SInputGroupAddon>
        </SInputGroup>
        <SButton
          type="button"
          class="h-20 w-[30%] shrink-0 text-3xl"
          :loading="isLoading"
          :disabled="!isPromocodeLengthValid"
          @click="applyIncertVoucher"
        >
          {{ $t("promocode.apply") }}
        </SButton>
      </div>
    </div>

    <div
      style="
        width: 80%;
        margin: auto;
        height: 80%;
        display: flex;
        flex-direction: row;
      "
    >
      <div
        style="letter-spacing: 1px; margin: 20px"
        v-for="voucherInfo in appliedVoucherInfos"
        :key="voucherInfo.voucherCode"
      >
        <p style="margin-bottom: 4px">Code: {{ voucherInfo.voucherCode }}</p>
        <p style="margin-bottom: 4px">Status: {{ voucherInfo.statusName }}</p>
        <p style="margin-bottom: 4px">
          Remaining Value:
          {{
            voucherInfo.partiallyRedeemable
              ? voucherInfo.originalRemainingValue / 100 -
                voucherInfo.currentSessionVoucherConsumption / 100 +
                "" +
                voucherInfo.currency /* Not partially redeemable vouchers are consumed fully if user confirms */
              : 0.0 + "" + voucherInfo.currency
          }}
        </p>
        <p style="margin-bottom: 4px">
          Partially redeemable:
          {{ voucherInfo.partiallyRedeemable ? "Yes" : "No" }}
        </p>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import BookingModule from "@/store/modules/booking";
import { computed, defineComponent, ref, watch, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import editPen from "@/assets/images/svg/edit-pen-rectangle.svg";
import { Currencies } from "@/constants/currency";
import { AccountingState, PaymentMethods } from "@/types/db/accounting";
import ScannerModule from "@/store/modules/scanner";
import {
  extractCodeFromUrl,
  removeCodePayment,
} from "@/utils/vue-helpers/promocode";
import {
  handleVoucherPayment,
  initiateVoucherService,
  // prepareOrderDataAndRedeem,
} from "@/api/external-vouchers/incert";
import ExternalVouchersModule from "@/store/modules/external-vouchers";
import {
  IExternalVoucher,
  VoucherService,
  VoucherStatusResponse,
  VoucherValidity,
} from "@/interfaces/store/IExternalVouchersState";
import { isBookingInDexie } from "@/utils/helpers/booking";
import { showConfirm } from "@/utils/vue-helpers/confirm-dialog";
import {
  SButton,
  SInputGroup,
  SInputGroupAddon,
  SInputGroupInput,
} from "@smeetz/ui-core";

export default defineComponent({
  name: "IncertVoucher",
  components: {
    SButton,
    SInputGroup,
    SInputGroupAddon,
    SInputGroupInput,
  },
  setup() {
    console.log("hi from IncertVoucher setup");
    const { t } = useI18n({});
    const editPenIconStyle = "width: 30px; height: 30px";
    const editPenIconContent = editPen;
    const editPenIconAlt = "Edit";
    const voucherCode = ref(
      "" /*getExistingPromocode()*/ /*BookingModule.appliedPromocode*/
    );
    const isLoading = ref(false);

    const isPromocodeLengthValid = computed(() => {
      return voucherCode.value.length >= 5;
    });

    const currency = computed(() => {
      return BookingModule.selectedBooking?.currency || Currencies.CHF;
    });

    const appliedVoucherInfos = computed(() => {
      const voucherInfos = ExternalVouchersModule.getExternalVouchers;
      const appliedVouchersAccountings =
        BookingModule.selectedBooking?.accountings.filter(
          (acc) =>
            acc.paymentMethod === PaymentMethods.EXTERNAL_INCERT_VOUCHER &&
            (acc.state === AccountingState.Pending ||
              acc.state === AccountingState.Completed)
        );
      if (appliedVouchersAccountings) {
        const codes = appliedVouchersAccountings.map((acc) => acc.promocode);
        return voucherInfos.filter((v) => codes.includes(v.voucherCode));
      }
      return [];
    });
    // const paymentType = ref(PaymentMethods.POS_PROMO_CODE);
    const successMessage = ref("");
    const inputPlaceholder = String(t("promocode.enter"));

    // function updatePaymentType(type: PaymentMethods) {
    //   paymentType.value = type;
    // }

    function setSuccessMessage() {
      successMessage.value = t("promocode.voucher-applied");
    }

    const isEditOrder = ref(false);

    watchEffect(async () => {
      if (BookingModule.selectedBookingUUID) {
        isEditOrder.value = await isBookingInDexie(
          BookingModule.selectedBookingUUID
        );
      }
    });

    const scanPromocodeModal = ref(false);
    const ScannerTog = computed(() => {
      return ScannerModule.tog;
    });

    watch(
      ScannerTog,
      async () => {
        if (ScannerModule.value && scanPromocodeModal.value) {
          LogService.info(
            `Incert Voucher: scanner triggered, value = ${ScannerModule.value}`
          );
          // The scanned valued can be a json object (Locarno logic) and can be a url (old logic for everyone else)

          // Check if the voucherCode is a URL
          const scannedValue = ScannerModule.value;
          const isUrl =
            String(scannedValue).startsWith("http://") ||
            String(scannedValue).startsWith("https://") ||
            String(scannedValue).startsWith("http//") ||
            String(scannedValue).startsWith("https//");

          if (isUrl) {
            LogService.info(`Incert Voucher: scanned value is in url format`);
            console.log(`Incert Voucher: scanned value is in url format`);
            // We keep the old logic of extracting the code from the url scanned
            const code = extractCodeFromUrl(ScannerModule.value);
            LogService.info(
              `Incert Voucher: extracted from qrcode string code: ${code}`
            );
            if (code && code.length >= 5) {
              voucherCode.value = code;
              LogService.info(
                `Incert Voucher: going to process scanned voucher code: ${voucherCode.value}`
              );
              await applyIncertVoucher();
              LogService.info(
                `Incert Voucher: Done processing scanned voucher code: ${voucherCode.value}`
              );
              scanPromocodeModal.value = false;
            }
          } else {
            LogService.info(
              `Incert Voucher: scanned value is not in url format. Checking if it is json object.`
            );
            // Check if the scanned value is actually a json object
            try {
              const scannedJsonCode = JSON.parse(scannedValue);
              LogService.info(
                `Incert Voucher: scanned value is in json format attached.`,
                { params: scannedJsonCode }
              );
              // Check if it's in the expected format
              if (
                scannedJsonCode &&
                scannedJsonCode.code &&
                scannedJsonCode.token
              ) {
                LogService.info(
                  `Incert Voucher: scanned value is exactly in the expected format.`,
                  { params: scannedJsonCode }
                );
                // Lets proceed applying the code
                voucherCode.value = scannedJsonCode.code;
                LogService.info(
                  `Incert Voucher: going to process scanned voucher code: ${voucherCode.value}`
                );
                await applyIncertVoucher();
                LogService.info(
                  `Incert Voucher: Done processing scanned voucher code: ${voucherCode.value}`
                );
                scanPromocodeModal.value = false;
              } else {
                LogService.info(
                  `Incert Voucher: scanned value is not in the expected format.`,
                  { params: scannedJsonCode }
                );
                // Lets notify the user that code couldn't be scanned
                NotificationService.showCustomMessage(
                  t("promocode.could-not-scan"),
                  "error"
                );
              }
            } catch (err) {
              // Couldn't parse the scanned value into a json object, this means the scanned value isn't a url and isn't a json obj, lets log
              LogService.info(
                `Incert Voucher Error: scanned value is neither a url nor a valid json object: ${voucherCode.value}`,
                { param: { scannedValue, err } },
                LogLevel.Error
              );
              // Lets notify the user that code couldn't be scanned
              NotificationService.showCustomMessage(
                t("promocode.could-not-scan"),
                "error"
              );
            }
          }
        }
      },
      { deep: true }
    );

    async function applyIncertVoucher() {
      console.log("----- applyIncertVoucher -------");
      console.log("voucherCode:", voucherCode.value);
      const isVoucherExist = ExternalVouchersModule.getExternalVouchers.find(
        (v) => v.voucherCode === voucherCode.value
      );
      const isVoucherPaymentsExist =
        BookingModule.selectedBooking?.accountings.find(
          (v) =>
            (v.state === AccountingState.Pending ||
              v.state === AccountingState.Completed) &&
            v.promocode === voucherCode.value
        );
      if (isVoucherExist && isVoucherPaymentsExist) {
        // No need to check because accountings are already created, it is too omplicated to redo accountings recreation just because user wants to check again
        return;
      } else if (isVoucherExist) {
        // voucher exist in the state but has no payments, lets erase what is in the state and store the new data
        const filteredVoucherInfo =
          ExternalVouchersModule.getExternalVouchers.filter(
            (v) => v.voucherCode !== voucherCode.value
          );
        if (filteredVoucherInfo) {
          ExternalVouchersModule.setExternalVouchers(filteredVoucherInfo);
        } else {
          ExternalVouchersModule.setExternalVouchers([]);
        }
      }

      console.log("instantiating voucherLibrary");
      const voucherLibObj = await initiateVoucherService();

      /**
       * TODO HAMZA
       * 1- checkStatus of the voucher to see if it is partially redeemable or not
       * 2- If fully redeemable then show the user a popup to confirm the full consumption of the voucher even if orderTotal is less than voucher amount
       * 3- If partially redeemable then
       *    (a) Prepare data as IExternalVoucherRedemption
       *    (b) Call library API to redeem the voucher
       *    (c) If success then show the usual notification
       *    (b) If error then show the usual notification with a new text based on the error
       */

      isLoading.value = true;

      let respData: VoucherStatusResponse | null | undefined;
      try {
        const resp = await voucherLibObj.getVoucherStatus({
          service: VoucherService.INCERT,
          code: voucherCode.value,
        });
        // const resp = await throwError();
        if (!resp.data) {
          LogService.info(
            "Incert Voucher Error: when checking code status",
            {
              params: resp,
            },
            LogLevel.Error
          );
          NotificationService.showGeneralResponseError();
          isLoading.value = false;
          return;
        }
        console.log("resp:", resp);
        respData = resp.data;
      } catch (error) {
        console.log("hi from catch");
        console.log(error);
        LogService.info(
          "Incert Voucher Error: when checking code status",
          {
            params: error,
          },
          LogLevel.Error
        );
        // NotificationService.showGeneralResponseError();
        NotificationService.showCustomError(t("error.no-internet"));
        // Lets log this error and return
        isLoading.value = false;
        return;
        // // Hard coded response
        // respData = {
        //   status: 1,
        //   remainingValue: 87199,
        //   partiallyRedeemable: true,
        //   currency: "EUR",
        // };
      }

      console.log("respData:", respData);
      const isNotValid =
        respData && (respData.status !== 1 || respData.remainingValue <= 0);
      if (isNotValid) {
        let msg = t("error.voucher-unkown");
        switch (respData?.status) {
          case VoucherValidity.Canceled:
            msg = t("error.voucher-canceled");
            break;
          case VoucherValidity.Expired:
            msg = t("error.voucher-expired");
            break;
          case VoucherValidity.FullyRedeemed:
            msg = t("error.voucher-fully-redeemed");
            break;
          case VoucherValidity.NotFound:
            msg = t("error.voucher-not-found");
            break;
          case VoucherValidity.Unknown:
            msg = t("error.voucher-unkown");
            break;
        }

        if (respData && respData.status === 1 && respData.remainingValue <= 0) {
          msg = t("error.voucher-fully-redeemed");
        }

        NotificationService.showCustomMessage(msg, "error");
        isLoading.value = false;
        return;
      }
      const isPartiallyRedeemable =
        respData &&
        respData /*resp.data?*/.partiallyRedeemable &&
        respData /*resp.data?*/.status === 1 &&
        respData /*resp.data?*/.remainingValue > 0;
      console.log("isPartiallyRedeemable:", isPartiallyRedeemable);

      isLoading.value = false;
      // If not partially redeemable then show the user a popup to confirm the full consumption of the voucher even if orderTotal is less than voucher amount
      if (!isPartiallyRedeemable) {
        const message = String(t("payment.incert-confirmation-popup"))
          .replace("xxAmount", String(respData?.remainingValue))
          .replace("xxCurrency", String(respData?.currency));
        const confirmed = await showConfirm({
          title: t("common.warning"),
          message,
          variant: "destructive",
        });
        if (!confirmed) {
          LogService.info(
            "User stopped redeeming the none-partially redeemable voucher"
          );
          return;
        }
        LogService.info(
          "User confirmed redeeming the none-partially redeemable voucher"
        );

        // Lets now store the voucher info in ExternalVouchersModule
        const statusName =
          VoucherValidity[
            Number(respData?.status) as unknown as keyof typeof VoucherValidity
          ];
        const voucherInfo: IExternalVoucher = {
          voucherCode: voucherCode.value,
          status: Number(respData?.status),
          // Now we set statusName based on VoucherValidity enum
          statusName: String(statusName),
          originalRemainingValue: Number(respData?.remainingValue) * 100, // Because Smeetz count in cents whereas Incert count in EUR
          partiallyRedeemable: respData?.partiallyRedeemable as boolean,
          currency: Currencies.EUR,
          orderSessionId: String(BookingModule.currentOrderSessionId),
          currentSessionVoucherConsumption: 0, // Current session voucher consumption
          voucherService: VoucherService.INCERT,
        };
        ExternalVouchersModule.addExternalVouchers(voucherInfo);
        LogService.info("Incert Voucher info added to the module", {
          params: voucherInfo,
        });

        // We will continue the flow as if the voucher is partially redeemable
        isLoading.value = true;
        handleVoucherPayment(voucherInfo);
        isLoading.value = false;
      } else {
        // Now lets continue the flow as if the voucher is partially redeemable or fully redeemable but user confirmed its full consumption
        isLoading.value = true;
        // Lets now store the voucher info in ExternalVouchersModule
        const statusName =
          VoucherValidity[
            Number(respData?.status) as unknown as keyof typeof VoucherValidity
          ];
        const voucherInfo: IExternalVoucher = {
          voucherCode: voucherCode.value,
          status: Number(respData?.status),
          statusName: String(statusName),
          originalRemainingValue: Number(respData?.remainingValue) * 100, // Because Smeetz count in cents whereas Incert count in EUR
          partiallyRedeemable: respData?.partiallyRedeemable as boolean,
          currency: Currencies.EUR,
          orderSessionId: String(BookingModule.currentOrderSessionId),
          currentSessionVoucherConsumption: 0, // Current session voucher consumption
          voucherService: VoucherService.INCERT,
        };
        ExternalVouchersModule.addExternalVouchers(voucherInfo);
        LogService.info("Incert Voucher info added to the module", {
          params: voucherInfo,
        });
        handleVoucherPayment(voucherInfo);
        // await prepareOrderDataAndRedeem(voucherCode.value, voucherLibObj);
        isLoading.value = false;
      }
    }

    return {
      appliedVoucherInfos,
      voucherCode,
      isLoading,
      editPenIconStyle,
      editPenIconContent,
      editPenIconAlt,
      isPromocodeLengthValid,
      t,
      successMessage,
      PaymentMethods,
      inputPlaceholder,
      setSuccessMessage,
      isEditOrder,
      scanPromocodeModal,
      currency,
      applyIncertVoucher,
      removeCodePayment,
    };
  },
  methods: {
    openScanPromocodeModal() {
      this.scanPromocodeModal = true;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;

.promocode-wrapper {
  // padding-top: 80px;
  .btn-height {
    height: 50px;
  }

  .inputs-wrapper {
    width: 80%;
  }

  .promocode-discount-result-wrapper {
    margin-top: 50px;
    width: 80%;

    .success-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      margin-bottom: 20px;
      color: #008000;
      font-weight: 600;
    }

    .discount-text {
      width: 90%;
      margin-left: auto;
      margin-right: auto;
      height: 110px;
      border-radius: 6px;
      background-color: #e5e5e5;
      font-size: 40px;
      color: $color-dark-grey;
    }
  }

  .options-wrapper {
    padding-bottom: 30px;

    .btn-option {
      justify-content: start;
      flex-grow: 1;
      text-align: left;
      border-radius: 9px;
      background-color: #e5e5e5;
      color: #000;
      border: 0;
    }

    .selected {
      background-color: #6aa6b7;
      color: #fff;
    }
  }
}

.remaining-amount {
  margin-left: 20px;
}

.remaining-amount-text {
  font-size: 0.9rem;
  font-weight: bolder;
  background-color: $color-background-green;
  border-radius: 5px;
  padding: 3px 15px;
}

.scan-promocode-modal .content {
  padding-bottom: 30px;
  font-size: 18px;
  font-weight: 700;
}
</style>
